import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { lcpDotColor } from "@/lib/lifecycle-matrix";

// IR-1228 P5b: provisional lifecycle colours (Rotem visual-gate — subject to
// change), keyed on the backend `*_group_status_color` name. IR-1425 r10 moved
// the table into `@/lib/lifecycle-matrix` (`lcpDotColor`) so this legacy cell
// and the matrix `LifecycleChip` cannot drift apart on one colour name.

export interface GroupStatusCellProps {
  status: string | null;
  color: string | null;
  label: string | null;
  missedCycles: number | null;
  failureKind: string | null;
  coveringAvatar: string | null;
  adminState: string | null;
}

export function GroupStatusCell({
  status,
  color,
  label,
  missedCycles,
  failureKind,
  coveringAvatar,
  adminState,
}: GroupStatusCellProps) {
  if (!status) return <span style={{ color: "var(--iris-fg-5)" }}>{"—"}</span>;
  const dot = lcpDotColor(color);

  const diagnostics: string[] = [];
  if (missedCycles != null && missedCycles > 0) diagnostics.push(`Missed cycles: ${missedCycles}`);
  if (failureKind) diagnostics.push(`Reason: ${failureKind}`);
  if (coveringAvatar) diagnostics.push(`Avatar: ${coveringAvatar}`);
  if (adminState) diagnostics.push(`Admin: ${adminState}`);

  const pill = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        data-status-dot
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: 999,
          background: dot,
          flexShrink: 0,
        }}
      />
      <span style={{ color: "var(--iris-fg-2)" }}>{label ?? status}</span>
    </span>
  );

  if (diagnostics.length === 0) return pill;

  // IR-1247: carry our own provider like the other tooltip cells
  // (avatars-cell / entity-groups-cell / field-info) so the component is valid
  // wherever it is mounted. Nesting under the App-root provider is harmless.
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span style={{ cursor: "help" }}>{pill}</span>
        </TooltipTrigger>
        <TooltipContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11 }}>
            {diagnostics.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
